import { DIALOG_THEME_MAPPING } from 'schema/src/kinds/dialog/theme';
import { POLL_THEME_MAPPING } from 'schema/src/kinds/poll/theme';
import { WEBVIEW_THEME_MAPPING } from 'schema/src/kinds/webview/theme';
import { ThemeCustomMapping } from 'schema/src/themes';
import { Poll, TrueTheme } from 'schema/src/types/poll.generated';
import { Poll as WebviewPoll } from 'schema/webview';
import { inIframe } from '../utils/location';
import { ROOT_TDS_ATTRIBUTE, ROOT_TUI_ATTRIBUTE } from './constants';
import { getColorsStyleContent, getFontsStyleContent, getOtherStyleContent } from './modern';

export const setThemeAttribute = (attributeName: string | string[], value: string) => {
  const attributes = Array.isArray(attributeName) ? attributeName : [attributeName];
  attributes.forEach(attributeName => {
    document.documentElement.setAttribute(attributeName, value);
  });
};

const getRootThemeAttribute = (template: Poll['template']) => {
  if (template === 'poll') {
    return ROOT_TUI_ATTRIBUTE;
  }

  return [ROOT_TUI_ATTRIBUTE, ROOT_TDS_ATTRIBUTE];
};

export const applyTuiOrTdsThemeHtmlAttribute = (
  theme: Partial<TrueTheme['data']>,
  template: ReturnType<typeof getPollTemplateForTheme>,
) => {
  const prefersColorScheme = getPrefersScheme(theme, template);
  setThemeAttribute(getRootThemeAttribute(template), prefersColorScheme);
};

export const getThemeCustomVariablesStyleContent = (template: Poll['template']) => {
  const themeMapping = getThemeMapping(getPollTemplateForTheme(template));
  const cssVariableStrings = generateCSSVariables(themeMapping.custom);

  return createStyleContent(cssVariableStrings, ':root');
};

const generateCSSVariables = (variables: ThemeCustomMapping): string[] => {
  return Object.keys(variables).map(key => `${key}: ${variables[key]};`);
};

export const createStyleContent = (styleProps: string[], selector: string) => {
  return `${selector} {\n${styleProps.join('\n')}\n}`;
};

export const injectThemeStyles = (styleContent: string) => {
  const styleElement = document.createElement('style');
  styleElement.appendChild(document.createTextNode(styleContent));
  document.head.appendChild(styleElement);
};

export const getPrefersScheme = (
  theme: Partial<TrueTheme['data']>,
  template: ReturnType<typeof getPollTemplateForTheme>,
): 'light' | 'dark' => {
  const themeForcedColorScheme = theme[template]?.forceColorScheme;

  if (themeForcedColorScheme) {
    return themeForcedColorScheme;
  }

  const urlParams = new URLSearchParams(window.location.search);
  const prefersSchema = urlParams.get('prefers-color-scheme');

  if (prefersSchema === 'dark' || prefersSchema === 'light') {
    return prefersSchema;
  }

  if (prefersSchema !== 'auto' && inIframe()) {
    return 'light';
  }

  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
};

export const getPollTemplateForTheme = (resolvedTemplate: Poll['template']) => {
  return resolvedTemplate === 'dialog_webview' ? 'dialog' : resolvedTemplate;
};

function getThemeMapping(template: ReturnType<typeof getPollTemplateForTheme>) {
  switch (template) {
    case 'webview':
      return WEBVIEW_THEME_MAPPING;
    case 'dialog':
      return DIALOG_THEME_MAPPING;
    default:
      return POLL_THEME_MAPPING;
  }
}

export const getThemeStyleContent = (
  theme: TrueTheme['data'],
  template: ReturnType<typeof getPollTemplateForTheme>,
): string => {
  const themeMapping = getThemeMapping(template);
  const colors = getColorsStyleContent(theme[template].colors, themeMapping.colors, template);
  if (template === 'poll') {
    const fonts = getFontsStyleContent(theme[template].fonts, themeMapping.fonts);
    const others = getOtherStyleContent(theme[template].others, themeMapping.others);

    return [colors, fonts, others].join('\n');
  }

  if (template === 'webview') {
    const others = getOtherStyleContent(theme[template].others, themeMapping.others);

    return [colors, others].join('\n');
  }

  //иначе это диалог, у него только цвета, fonts и others - нет
  return [colors].join('\n');
};

export function injectStatusBarColor(
  theme: TrueTheme['data'],
  template: ReturnType<typeof getPollTemplateForTheme>,
  flavour: WebviewPoll['flavour'] | null | undefined,
) {
  const prefersColorScheme = getPrefersScheme(theme, template);

  if (template === 'webview' && flavour === 'mobile_bank') {
    const color = prefersColorScheme === 'light' ? '#ffffff' : '#1c1c1e';
    addPseudoStatusBarBackground(color);
    setMetaThemeColor(color);
  }
}

function addPseudoStatusBarBackground(color: string) {
  const statusBar = document.createElement('div');

  statusBar.style.width = '100%';
  statusBar.style.height = 'env(safe-area-inset-top)';
  statusBar.style.backgroundColor = color;
  statusBar.style.position = 'fixed';
  statusBar.style.top = '0';
  statusBar.style.left = '0';
  statusBar.style.zIndex = '100000';

  document.body.appendChild(statusBar);
}

function setMetaThemeColor(color: string) {
  let meta: HTMLMetaElement | null = document.querySelector('meta[name="theme-color"]');

  if (!meta) {
    meta = document.createElement('meta');
    meta.name = 'theme-color';
    document.head.appendChild(meta);
  }

  meta.content = color;
}
